<template>
  <div class="measureAssetTable"
    v-loading="assetsLoading"
  >
    <neb-table v-if="rows.length"
      :columns="computedColumns"
      :rows="rows"
      :totals="totals"
      totalsAtTop
      :lockColumns="[ 'title' ]"
    >
      <template v-slot:cell--title="{ cellValue, rowIdx }">
        <div class="measureAssetTable-title">
          <neb-checkbox :value="assetIsSelected(rowIdx)"
            @click.native="e => onCheckboxClick(e, rowIdx)"
          ></neb-checkbox>
          <router-link :to="detailRoute(rowIdx)"
            target="_blank"
            rel="noopener noreferrer"
            :title="cellValue"
          >
            <span>
              {{ truncate(cellValue, 44) }}
            </span>
          </router-link>
        </div>
      </template>

      <template v-slot:cell--keywords="{ cellValue }">
        <value-list-cell :values="cellValue"></value-list-cell>
      </template>

      <template v-slot:cell--topics="{ cellValue }">
        <value-list-cell :values="cellValue"></value-list-cell>
      </template>

      <template v-slot:cell--urls="{ cellValue }">
        <source-urls :items="cellValue"></source-urls>
      </template>

      <template v-slot:cell--date="{ cellValue }">
        <span class="assetTable-date-date">{{ formatDate(cellValue.date) }}</span>
        <br>
        <span class="assetTable-date-type">{{ capitalize(cellValue.type) }}</span>
      </template>

      <template v-slot:cell--properties="{ cellValue }">
        <value-list-cell :values="cellValue"></value-list-cell>
      </template>
    </neb-table>

    <div class="measureAssetTable-pagination"
      v-if="rows.length"
    >
      <neb-paginator :currentPage="page"
        :pageSize="12"
        :totalCount="totalAssetCount"
        @change="onPageChange"
      ></neb-paginator>
    </div>

    <div class="measureAssetTable-noAssets"
      v-show="rows.length < 1"
    >
      <div class="measureAssetTable-noAssets-logo">
        <p>Sorry! No results were found.</p>
      </div>
    </div>
  </div>
</template>

<script>
import { capitalize, has, includes, without, range, uniq } from 'lodash'
import moment from 'moment'
import {
  formatDuration,
  formatInt,
  formatPercent,
  formatFloat
} from '@/helpers'

import ValueListCell from './ValueListCell'
import SourceUrls from './SourceUrls'

const columnMap = {
  title: { label: 'Content Title' },
  source: { label: 'Source' },
  urls: { label: 'URL' },
  date: { label: 'Date' },
  page_views: { label: 'Total Views', justify: 'right' },
  social_actions: { label: 'Total Social Actions', justify: 'right' },
  avg_pct_consumed: { label: 'Avg. Percent Consumed', justify: 'right' },
  conversions: { label: 'Total Conversions', justify: 'right' },
  avg_duration: { label: 'Duration', justify: 'right' },
  avg_word_count: { label: 'Word Count', justify: 'right' },
  avg_scroll_depth: { label: 'Avg. Scroll Depth Percent', justify: 'right' },
  keywords: { label: 'Keywords'},
  topics: { label: 'Topics' },
  properties: { label: 'Properties' },
  is_sponsored: { label: 'Is Sponsored' },
  scroll_initiations: { label: 'Total Scroll Initiations', justify: 'right' },
  pct_scroll_initiations: { label: 'Scroll Initiation Rate', justify: 'right' },
  likes: { label: 'Total Social Likes', justify: 'right' },
  comments: { label: 'Total Social Comments', justify: 'right' },
  shares: { label: 'Total Social Shares', justify: 'right' },
  active_time: { label: 'Total Time on Content', justify: 'right' },
  avg_active_time: { label: 'Avg. Time on Content', justify: 'right' },
  avg_completion: { label: 'Completion Rate', justify: 'right' },
  pct_conversions: { label: 'Conversion Rate', justify: 'right' }
}

const getSourceIcon = () => {
    return require('../../assets/images/toggle-box-images/web.svg')
}

// These functions are for formatting values
// Used to extract the values from an asset for a specific column.
const columnToAssetFieldMap = {
  // Metadata
  title: a => a.title,
  source: a => capitalize(a.data_provider),
  urls: a => [{ icon: getSourceIcon(), url: a.url }],
  date: a => ({ date: a.created_at, type: 'created' }),
  avg_duration: a => formatDuration(a.duration),
  avg_word_count: a => formatInt(a.word_count),
  keywords: a => a.keywords,
  topics: a => a.topics,
  properties: a => uniq(a.asset_properties
    .map(p => p.split('__'))
    .filter(p => p[0] !== '')
    .map(p => `${p[0]}: ${p[1]}`)),
  is_sponsored: a => a.is_sponsored,

  // Metrics
  page_views: a => formatInt(a.metrics.PAGE_VIEWS),
  scroll_initiations: a => formatInt(a.metrics.SCROLL_INITIATIONS),
  pct_scroll_initiations: a => formatPercent(a.metrics.PCT_SCROLL_INITIATIONS),
  social_actions: a =>  formatInt(a.metrics.SOCIAL_ACTIONS),
  likes: a => formatInt(a.metrics.LIKES),
  comments: a => formatInt(a.metrics.COMMENTS),
  shares: a => formatInt(a.metrics.SHARES),
  avg_pct_consumed: a => formatPercent(a.metrics.AVG_PCT_CONSUMED),
  conversions: a => formatInt(a.metrics.CONVERSIONS),
  pct_conversions: a => formatPercent(a.metrics.PCT_CONVERSIONS),
  avg_scroll_depth: a => formatPercent(a.metrics.AVG_SCROLL_DEPTH),
  active_time: a => formatDuration(a.metrics.ACTIVE_TIME),
  avg_active_time: a => formatDuration(a.metrics.AVG_ACTIVE_TIME),
  avg_completion: a => formatPercent(a.metrics.AVG_COMPLETION)
}

// Used to extract totals row data from a query level metrics array.
const totalsColumnToQueryMetricsMap = {
  avg_duration: m => `Avg: ${formatDuration(m.AVG_DURATION)}`,
  avg_word_count: m => `Avg: ${formatFloat(m.AVG_WORD_COUNT)}`,
  page_views: m => formatInt(m.PAGE_VIEWS),
  scroll_initiations: m => formatInt(m.SCROLL_INITIATIONS),
  pct_scroll_initiations: m => `Avg: ${formatPercent(m.PCT_SCROLL_INITIATIONS)}`,
  social_actions: m =>  formatInt(m.SOCIAL_ACTIONS),
  likes: m => formatInt(m.LIKES),
  comments: m => formatInt(m.COMMENTS),
  shares: m => formatInt(m.SHARES),
  avg_pct_consumed: m => `Avg: ${formatPercent(m.AVG_PCT_CONSUMED)}`,
  conversions: m => formatInt(m.CONVERSIONS),
  pct_conversions: m => formatPercent(m.PCT_CONVERSIONS),
  avg_scroll_depth: m => `Avg: ${formatPercent(m.AVG_SCROLL_DEPTH)}`,
  active_time: m => formatDuration(m.ACTIVE_TIME),
  avg_active_time: m => formatDuration(m.AVG_ACTIVE_TIME),
  avg_completion: m =>  `Avg: ${formatPercent(m.AVG_COMPLETION)}`
}

// Takes an asset object as returned from the api and transforms it into a
// row object to be used by the table.
const assetToRow = (asset, columns) =>
  Object.fromEntries(
    columns
      .map(col => [col, columnToAssetFieldMap[col](asset)])
  )

// Takes the query level metrics returned by the endpoint and transforms them
// into a totals row object.
const queryMetricsToTotalsRow = (metrics, columns) =>
  Object.fromEntries(
    columns
      .filter(col => has(totalsColumnToQueryMetricsMap, col))
      .map(col => [col, totalsColumnToQueryMetricsMap[col](metrics)])
  )

export default {
  name: 'MeasureAssetTable',

  components: {
    ValueListCell,
    SourceUrls
  },

  props: {
    assets: {
      type: Array,
      default: () => []
    },
    assetsLoading: {
      type: Boolean,
      default: false
    },
    selectedAssetIndexes: {
      type: Array,
      default: () => []
    },
    page: {
      type: Number,
      default: 1
    },
    totalAssetCount: {
      type: Number,
      default: 0
    },
    columns: {
      type: Array,
      default: () => []
    },
    queryMetrics: {
      type: Object,
      default: () => ({})
    }
  },

  computed: {
    computedColumns () {
      return this.columns
        .map(c => Object.assign({}, columnMap[c], { key: c }))
    },
    rows () {
      return this.assets
        .map(a => assetToRow(a, this.columns))
    },
    totals () {
      return Object.assign(
        {},
        queryMetricsToTotalsRow(this.queryMetrics, this.columns),
        { title: `Total Assets: ${formatInt(this.totalAssetCount)}` }
      )
    },
    computedSelectedAssetIndexes: {
      get () {
        return this.selectedAssetIndexes
      },
      set (value) {
        this.$emit('selected-asset-indexes-changed', value)
      }
    }
  },

  methods: {
    formatDate (d) {
      return moment(d).format('MM/DD/YYYY')
    },
    capitalize: capitalize,
    onPageChange (newPage) {
      this.$emit('page-change', newPage)
    },
    detailRoute (rowIdx)  {
      const asset = this.assets[rowIdx]

      return {
        name: 'assetDetail',
        params: {
          brandName: this.$route.params.brandName,
          pageUrlId: asset.page_url_id
        }
      }
    },
    truncate (value, length) {
      return value.length > length
        ? `${value.substring(0, length)}...`
        : value
    },
    assetIsSelected (idx) {
      return includes(this.computedSelectedAssetIndexes, idx)
    },
    onCheckboxClick (e, idx) {
      if (e.shiftKey) {
        this.onAssetShiftSelect(idx)
      } else {
        this.onAssetSelect(idx)
      }
    },
    onAssetSelect (idx) {
      if (this.assetIsSelected(idx)) {
        this.computedSelectedAssetIndexes = without(this.computedSelectedAssetIndexes, idx)
      } else {
        const newIndexes = this.computedSelectedAssetIndexes.slice()

        newIndexes.push(idx)
        newIndexes.sort((a, b) => a - b)
        this.computedSelectedAssetIndexes = newIndexes
      }
    },
    onAssetShiftSelect (idx) {
      const lastSelectedIndex = this.computedSelectedAssetIndexes
        .reduce((acc, val) => {
          if (val > acc && val < idx) {
            return val
          } else {
            return acc
          }
        }, 0)

      const newSelectedIndexes = range(lastSelectedIndex, idx + 1)

      const selectedIndexesCopy = this.computedSelectedAssetIndexes.slice()
      const mergedIndexes = selectedIndexesCopy.concat(newSelectedIndexes)
        .filter(idx_ => idx_ <= idx)

      this.computedSelectedAssetIndexes = uniq(mergedIndexes)
    }
  }
}
</script>

<style lang="scss" scoped>
.measureAssetTable {
  padding-left: 20px;
  padding-bottom: 80px;
  width: 100%;

  &-title {
    width: 200px;
    display: flex;

    span {
      display: block;
      white-space: initial;
      cursor: pointer;
      margin-left: 7px;
    }
  }

  &-pagination {
    display: flex;
    justify-content: flex-end;
    align-items: center;
    margin-top: 20px;
  }

  &-noAssets {
    width: 100%;
    height: 280px;
    display: flex;
    justify-content: center;
    align-items: center;
    font-size: 22px;
    font-weight: 500;

    &-logo {
      height: 200px;
      background: no-repeat center url('../../assets/images/no-results.svg');
      p {
        position: relative;
        top: 52px;
      }
    }
  }
}
</style>
